// AI assisted development
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

type LazyModule<T extends ComponentType<object>> = Promise<{ default: T }>;

const RETRY_STORAGE_PREFIX = 'lazy-import-retry:';

export const pageReloader = {
  reload(): void {
    window.location.reload();
  },
};

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();

  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('loading chunk') ||
    message.includes('chunkloaderror')
  );
}

/**
 * Reload once when a lazy-loaded chunk is stale after a deployment.
 * This recovers users who still have an older bundle cached in memory.
 */
export function lazyWithRetry<T extends ComponentType<object>>(
  importer: () => LazyModule<T>,
  moduleKey: string,
): LazyExoticComponent<T> {
  return lazy(async () => {
    const retryStorageKey = `${RETRY_STORAGE_PREFIX}${moduleKey}`;

    try {
      const module = await importer();
      window.sessionStorage.removeItem(retryStorageKey);
      return module;
    } catch (error) {
      const hasRetried = window.sessionStorage.getItem(retryStorageKey) === 'true';

      if (isChunkLoadError(error) && !hasRetried) {
        window.sessionStorage.setItem(retryStorageKey, 'true');
        pageReloader.reload();

        // Keep the promise pending while the browser reloads the page.
        return new Promise<never>(() => {});
      }

      window.sessionStorage.removeItem(retryStorageKey);
      throw error;
    }
  });
}
